import React, { useState, useEffect, useContext } from 'react';
import { Container, Box, Grid } from '@mui/material';
import { PlayerSelection } from '../../../components/playerSelection/playerSelection';
import { di } from '../../../state/inversify.config';
import { validateEmail } from '../conneqt.helper';
import { UserNotificationContext } from '../../../components/userNotification/userNotificationContext';
import { UserNotificationType } from '../../../components/userNotification/userNotificationType.enum';
import { AddConneqtDomain } from './addConneqt.domain';
import { TextInput } from '../../../components/textInput/textInput';
import { AlertDialog } from '../../../components/alertDialog/alertDialog';
import { useCustomTranslation } from '../../../translation/customTranslation';
import { Button } from '../../../components/button/button';

interface AddConneqtProps {
  showAddConnectModal?: any;
}
export function AddConneqt({ showAddConnectModal }: AddConneqtProps) {
  const { t } = useCustomTranslation();

  const [showSendEmailInviteModal, setShowSendEmailInviteModal] =
    useState(false);

  const { addUserNotification } = useContext(UserNotificationContext);

  const addConneqtDomain = di.get(AddConneqtDomain);
  const selectedPlayerClues = addConneqtDomain.useSelectedPlayerClues();
  const { selectedPlayerId, responseEmail } =
    addConneqtDomain.usePendingConneqtInfoSelector();

  const conneqtWithEmailNotFound =
    addConneqtDomain.useConneqtWithEmailNotFoundSelector();
  const pendingConneqt = addConneqtDomain.usePendingConneqtSelector();
  const alreadyConneqted = addConneqtDomain.useAlreadyConneqtedSelector();

  const [inputs, setInputs] = useState<{ [key: string]: string }>({
    responseEmail: '',
  });

  const loggedInUser = addConneqtDomain.useUserSelector();

  useEffect(() => {
    if (conneqtWithEmailNotFound) {
      setShowSendEmailInviteModal(true);
      resetAddConneqtForm();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conneqtWithEmailNotFound]);

  const handleSubmit = () => {
    // TODO: move validation into domain service or even thunk and return error message or use global error display module?
    // TODO: Add error banner when design system component is ready
    if (!loggedInUser?.email) return;
    if (
      inputs.responseEmail?.toLowerCase() === loggedInUser?.email.toLowerCase()
    ) {
      addUserNotification(
        UserNotificationType.ERROR,
        t('UNABLE_TO_SEND_CONNEQT_TO_LOGGED_IN_ACCOUNT'),
        UserNotificationType.ERROR,
      );
      resetAddConneqtForm();
      return;
    }
    if (!inputs.responseEmail) {
      addUserNotification(
        UserNotificationType.ERROR,
        t('ALL_FIELDS_REQUIRED'),
        UserNotificationType.ERROR,
      );
      return;
    }

    if (!validateEmail(inputs.responseEmail)) {
      addUserNotification(
        UserNotificationType.ERROR,
        t('SIGN_UP_VALIDATE_EMAIL'),
        UserNotificationType.ERROR,
      );
      return;
    }

    if (
      selectedPlayerId === undefined ||
      !selectedPlayerId ||
      selectedPlayerId === t('ID_MODULE_SELECT_PLACEHOLDER')
    ) {
      addUserNotification(
        UserNotificationType.ERROR,
        t('PLAYER_SELECTION_REQUIRED'),
        UserNotificationType.ERROR,
      );
      return;
    }

    if (selectedPlayerClues === undefined) {
      addUserNotification(
        UserNotificationType.ERROR,
        t('UNABLE_TO_CREATE_CONNEQT'),
        UserNotificationType.ERROR,
      );
      return;
    }

    if (alreadyConneqted || pendingConneqt) {
      addUserNotification(
        UserNotificationType.ERROR,
        t('CONNEQT_ALREADY_EXISTS'),
        UserNotificationType.ERROR,
      );
      return;
    }

    addConneqtDomain.dispatchCreateConneqt({
      requestPlayerId: selectedPlayerId,
      requestPlayerClues: selectedPlayerClues,
      responseEmail: inputs.responseEmail,
    });

    // TODO: clear view state and dispatch conneqtId so view scroll down to newly created conneqt.

    showAddConnectModal(false);
    addUserNotification(
      UserNotificationType.SUCCESS,
      t('CONNEQT_REQUEST_CREATED'),
      UserNotificationType.SUCCESS,
    );
  };

  // TODO Add confirmation modal to double check the email
  const handleSendInvite = (id: string) => {
    addConneqtDomain.dispatchInviteSentStatus(id);
    setShowSendEmailInviteModal(false);
    addUserNotification(
      UserNotificationType.SUCCESS,
      t('CONNEQT_REQUEST_CREATED'),
      UserNotificationType.SUCCESS,
    );
  };

  const resetAddConneqtForm = () => {
    addConneqtDomain.dispatchResponseEmail('');
  };

  const onSelect = (playerId: string) => {
    addConneqtDomain.dispatchSelectedPlayerId(playerId);
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;

    setInputs(() => ({
      [name]: value,
    }));
  };

  return (
    <Container maxWidth="md">
      <Grid>
        <Box>
          <PlayerSelection
            accountType={loggedInUser?.accountType}
            onSelect={onSelect}
          />
          <TextInput
            name="responseEmail"
            type="email"
            value={inputs.responseEmail}
            label={t('RECEIVER_EMAIL_ADDRESS')}
            required
            onChange={handleInputChange}
            fullWidth
          />

          <Button
            testId="sendRequest"
            type="submit"
            fullWidth
            variant="contained"
            color="secondary"
            sx={{ my: 2 }}
            onClick={handleSubmit}
            label={t('SEND_REQUEST')}
          />
        </Box>
        <AlertDialog
          title={t('UNABLE_TO_FIND_ACCOUNT')}
          setOpen={setShowSendEmailInviteModal}
          open={showSendEmailInviteModal}
          content={t('WOULD_YOU_LIKE_TO_SEND_INVITE')}
          actions
          onConfirm={() =>
            handleSendInvite(conneqtWithEmailNotFound?.id as string)
          }
        />
      </Grid>
    </Container>
  );
}
